import { useState } from "react";
import { LegalLayout, LegalH2, LegalP, LegalUl } from "@/components/LegalLayout";

const Strong = ({ children }) => <strong className="text-white/85">{children}</strong>;

const TermsIT = () => (
  <>
    <LegalP>
      Utilizzando Liqo (“l'App”) accetti i presenti Termini e Condizioni. Se non li accetti, non utilizzare l'App.
    </LegalP>

    <LegalH2>Descrizione del servizio</LegalH2>
    <LegalP>
      Liqo è uno strumento di finanza personale che aggrega i tuoi dati finanziari (Open Banking PSD2, PayPal,
      portafoglio manuale) e fornisce categorizzazione automatica, insight, previsioni di cassa, stime fiscali
      (Regime Forfettario) e un assistente conversazionale (“CFO AI”).
    </LegalP>

    <LegalH2>Nessuna consulenza finanziaria</LegalH2>
    <LegalP>
      I contenuti dell'App, incluse stime fiscali, suggerimenti del Coach Investimenti, segnali (compra/tieni/vendi)
      ed esempi educativi (es. ETF, PAC, obbligazioni), hanno finalità esclusivamente informativa ed educativa e{" "}
      <Strong>non costituiscono consulenza finanziaria, di investimento, fiscale o legale</Strong>, né una
      sollecitazione all'investimento. Le decisioni finanziarie sono di tua esclusiva responsabilità; valuta di
      consultare un professionista abilitato.
    </LegalP>

    <LegalH2>Account</LegalH2>
    <LegalUl
      items={[
        "Sei responsabile della riservatezza delle tue credenziali e delle attività svolte dal tuo account.",
        "Devi fornire informazioni veritiere e avere l'età minima richiesta dalla legge applicabile.",
        "Puoi eliminare il tuo account in qualsiasi momento dalle impostazioni.",
      ]}
    />

    <LegalH2>Open Banking e dati di terzi</LegalH2>
    <LegalP>
      Il collegamento bancario avviene tramite il provider autorizzato PSD2 <Strong>Enable Banking</Strong> in sola
      lettura e richiede il tuo consenso presso la banca. L'accuratezza e la disponibilità dei dati dipendono da
      banche e fornitori terzi; non garantiamo la continuità o l'esattezza dei dati forniti da questi soggetti.
    </LegalP>

    <LegalH2>Uso consentito</LegalH2>
    <LegalUl
      items={[
        "Non utilizzare l'App per scopi illeciti o in violazione di diritti di terzi.",
        "Non tentare di accedere a dati di altri utenti o compromettere la sicurezza del servizio.",
        "Non effettuare reverse engineering non consentito dalla legge.",
      ]}
    />

    <LegalH2>Limitazione di responsabilità</LegalH2>
    <LegalP>
      Nei limiti consentiti dalla legge, l'App è fornita “così com'è”, senza garanzie. Non saremo responsabili per
      perdite derivanti da decisioni prese sulla base delle informazioni dell'App, da imprecisioni dei dati di terzi
      o da interruzioni del servizio.
    </LegalP>

    <LegalH2>Modifiche</LegalH2>
    <LegalP>
      Possiamo aggiornare il servizio e i presenti Termini. Le modifiche sostanziali saranno comunicate nell'App.
      L'uso continuato dopo l'aggiornamento costituisce accettazione.
    </LegalP>

    <LegalH2>Legge applicabile</LegalH2>
    <LegalP>
      I presenti Termini sono regolati dalla legge italiana. Foro competente: [città], salvo norme inderogabili a
      tutela del consumatore. (Da completare prima della pubblicazione.)
    </LegalP>

    <LegalH2>Contatti</LegalH2>
    <LegalP>Per assistenza: supporto@liqo.finance.</LegalP>
  </>
);

const TermsEN = () => (
  <>
    <LegalP>
      By using Liqo (“the App”) you accept these Terms &amp; Conditions. If you do not accept them, do not use the App.
    </LegalP>

    <LegalH2>Service description</LegalH2>
    <LegalP>
      Liqo is a personal finance tool that aggregates your financial data (Open Banking PSD2, PayPal, manual
      portfolio) and provides automatic categorisation, insights, cash-flow forecasts, tax estimates (Italian
      “Regime Forfettario”) and a conversational assistant (“AI CFO”).
    </LegalP>

    <LegalH2>No financial advice</LegalH2>
    <LegalP>
      The App's content — including tax estimates, Investment Coach suggestions, signals (buy/hold/sell) and
      educational examples (e.g. ETFs, recurring investment plans, bonds) — is for informational and educational
      purposes only and{" "}
      <Strong>does not constitute financial, investment, tax or legal advice</Strong>, nor a solicitation to invest.
      Financial decisions are your sole responsibility; consider consulting a licensed professional.
    </LegalP>

    <LegalH2>Account</LegalH2>
    <LegalUl
      items={[
        "You are responsible for the confidentiality of your credentials and for activities carried out under your account.",
        "You must provide truthful information and meet the minimum age required by applicable law.",
        "You can delete your account at any time from the settings.",
      ]}
    />

    <LegalH2>Open Banking and third-party data</LegalH2>
    <LegalP>
      Bank linking is performed through the PSD2-authorised provider <Strong>Enable Banking</Strong> in read-only
      mode and requires your consent at your bank. The accuracy and availability of data depend on banks and
      third-party providers; we do not guarantee the continuity or accuracy of data provided by such parties.
    </LegalP>

    <LegalH2>Acceptable use</LegalH2>
    <LegalUl
      items={[
        "Do not use the App for unlawful purposes or in violation of third-party rights.",
        "Do not attempt to access other users' data or compromise the security of the service.",
        "Do not perform reverse engineering not permitted by law.",
      ]}
    />

    <LegalH2>Limitation of liability</LegalH2>
    <LegalP>
      To the extent permitted by law, the App is provided “as is”, without warranties. We are not liable for losses
      arising from decisions made on the basis of the App's information, from inaccuracies in third-party data, or
      from service interruptions.
    </LegalP>

    <LegalH2>Changes</LegalH2>
    <LegalP>
      We may update the service and these Terms. Material changes will be notified in the App. Continued use after
      the update constitutes acceptance.
    </LegalP>

    <LegalH2>Governing law</LegalH2>
    <LegalP>
      These Terms are governed by Italian law. Competent court: [city], save for mandatory consumer-protection
      rules. (To be completed before publication.)
    </LegalP>

    <LegalH2>Contact</LegalH2>
    <LegalP>For support: supporto@liqo.finance.</LegalP>
  </>
);

export default function Terms() {
  const [lang, setLang] = useState("it");
  const meta =
    lang === "it"
      ? { title: "Termini e Condizioni", updated: "Ultimo aggiornamento: Giugno 2026" }
      : { title: "Terms & Conditions", updated: "Last updated: June 2026" };
  return (
    <LegalLayout title={meta.title} updated={meta.updated} testid="terms-page" lang={lang} setLang={setLang}>
      {lang === "it" ? <TermsIT /> : <TermsEN />}
    </LegalLayout>
  );
}
